// One watch session is one visit to a video or audio item: from the first play
// until the teacher leaves. Raw play/pause events say what happened; this
// keeps the running totals that cannot be rebuilt from them afterwards
// (seconds actually played, distinct seconds seen), and tags every event with
// the session id so the server can group them.
//
// Totals ride on every heartbeat and on the final exit event as cumulative
// values, so a phone that is switched off or killed mid-video still leaves the
// latest figures behind: the server takes the maximum, it does not need the
// exit.

export const HEARTBEAT_INTERVAL_MS = 10_000;
// A timeupdate further apart than this is a jump (a seek), not playback.
const MAX_PLAYBACK_STEP_SECONDS = 2;
// Dragging the scrubber fires many seek events; they count as one seek, from
// where the drag began to where it ended, once the position has settled.
const SEEK_SETTLE_MS = 600;
// Smaller moves are the player nudging the position, not the teacher seeking.
const MIN_SEEK_SECONDS = 1;
// A pause event this close to the end is the player stopping at the end of the
// file, which is reported as "ended", not as the teacher pausing.
const END_TOLERANCE_SECONDS = 0.5;

export interface WatchContext {
  /** The downloaded copy on the phone, or streamed from the server. */
  source: "offline_copy" | "online";
  ui_language: string;
}

export interface WatchEnvironment {
  emit(type: string, payload: Record<string, unknown>): void;
  /** Read per event: the source is only known once the offline lookup finishes. */
  context(): WatchContext;
  /** Distinct seconds ever watched of this item across all visits, if tracked. */
  itemUniqueSeconds?(): number;
  now?(): number;
}

export class WatchSession {
  readonly id = crypto.randomUUID();
  private readonly env: WatchEnvironment;
  private readonly previously: ReadonlySet<number>;
  private readonly seen = new Set<number>();
  private readonly fresh = new Set<number>();
  private playSeconds = 0;
  private lastTime: number | null = null;
  private position = 0;
  private duration = 0;
  private lastHeartbeat = 0;
  private started = false;
  private reachedEnd = false;
  private exited = false;
  private seekOrigin: number | null = null;
  private seekTarget = 0;
  private seekTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * @param previouslyWatched seconds of this item watched on earlier visits, so
   * seconds seen for the first time can be told from re-watched ones.
   */
  constructor(env: WatchEnvironment, previouslyWatched: ReadonlySet<number> = new Set()) {
    this.env = env;
    this.previously = new Set(previouslyWatched);
  }

  private now(): number {
    return this.env.now ? this.env.now() : Date.now();
  }

  private send(type: string, extra: Record<string, unknown>): void {
    this.env.emit(type, {
      watch_session_id: this.id,
      duration: this.duration || null,
      ...this.env.context(),
      ...extra,
    });
  }

  private totals(): Record<string, unknown> {
    const itemUnique = this.env.itemUniqueSeconds?.();
    return {
      position_seconds: this.position,
      play_seconds: Math.round(this.playSeconds * 10) / 10,
      session_unique_seconds: this.seen.size,
      session_new_seconds: this.fresh.size,
      ...(itemUnique === undefined ? {} : { unique_seconds_watched: itemUnique }),
    };
  }

  play(position: number, duration: number, rate = 1): void {
    this.position = position;
    this.duration = duration || this.duration;
    this.lastTime = position;
    // The first heartbeat is a heartbeat-interval after starting, not at once.
    if (!this.started) this.lastHeartbeat = this.now();
    this.started = true;
    this.send("media_play", { position_seconds: position, rate });
  }

  pause(position: number, duration: number, ended = false): void {
    this.position = position;
    this.duration = duration || this.duration;
    if (ended || (this.duration > 0 && position >= this.duration - END_TOLERANCE_SECONDS)) return;
    this.send("media_pause", this.totals());
  }

  timeUpdate(position: number, duration: number, seeking: boolean): void {
    this.duration = duration || this.duration;
    if (seeking) {
      this.lastTime = null;
      return;
    }
    const previous = this.lastTime;
    if (
      previous !== null &&
      position >= previous &&
      position - previous <= MAX_PLAYBACK_STEP_SECONDS
    ) {
      this.playSeconds += position - previous;
      for (let second = Math.floor(previous); second <= Math.floor(position); second += 1) {
        this.seen.add(second);
        if (!this.previously.has(second)) this.fresh.add(second);
      }
    }
    this.lastTime = position;
    this.position = position;
    if (this.now() - this.lastHeartbeat >= HEARTBEAT_INTERVAL_MS) this.heartbeat();
  }

  /** The teacher started to move the position. `position` is where it was. */
  seeking(): void {
    this.lastTime = null;
    if (this.seekOrigin === null) this.seekOrigin = this.position;
  }

  /** The move finished at `position`; settled moves become one seek event. */
  seeked(position: number): void {
    if (this.seekOrigin === null) return;
    this.seekTarget = position;
    if (this.seekTimer) clearTimeout(this.seekTimer);
    this.seekTimer = setTimeout(() => this.settleSeek(), SEEK_SETTLE_MS);
  }

  private settleSeek(): void {
    if (this.seekTimer) clearTimeout(this.seekTimer);
    this.seekTimer = null;
    const from = this.seekOrigin;
    this.seekOrigin = null;
    if (from === null) return;
    const to = this.seekTarget;
    this.position = to;
    this.lastTime = to;
    if (Math.abs(to - from) < MIN_SEEK_SECONDS) return;
    this.send("media_seek", {
      from_seconds: from,
      to_seconds: to,
      direction: to > from ? "forward" : "back",
    });
  }

  ended(position: number, duration: number): void {
    this.position = position;
    this.duration = duration || this.duration;
    this.reachedEnd = true;
    this.send("media_ended", this.totals());
  }

  /** The page is going out of view: record the latest figures now. */
  checkpoint(): void {
    if (this.started && !this.exited) this.heartbeat();
  }

  private heartbeat(): void {
    this.lastHeartbeat = this.now();
    this.send("media_heartbeat", this.totals());
  }

  /** True once the teacher has left; a later play starts a new session. */
  get finished(): boolean {
    return this.exited;
  }

  /** The teacher left the item. Safe to call more than once. */
  exit(): void {
    if (this.exited) return;
    this.exited = true;
    if (this.seekOrigin !== null) this.settleSeek();
    if (!this.started) return;
    this.send("media_exit", { ...this.totals(), reached_end: this.reachedEnd });
  }
}
